/**
 * Fallstudien und österreichische Marktzahlen für die Lernstrecke.
 *
 * Ergänzungsoption 4 aus der Deckungsanalyse für Junior Achievement Austria
 * (`public/skills4retail-49130437.htm`): drei Fallstudien aus dem
 * Skills4Retail-Material in gekürzter Form und Marktzahlen für Österreich,
 * jeweils mit Quellenangabe.
 *
 * **Warum eine eigene Datei und nicht mitten in `chapters.js`:** Alles hier
 * geht auf **fremdes Material** zurück, der übrige Kurstext nicht. Diese Grenze
 * muss sichtbar bleiben — sonst weiß in einem Jahr niemand mehr, welcher Absatz
 * eine Quellenangabe braucht und welcher aus dem eigenen Haus stammt. Aus
 * demselben Grund trägt jeder Baustein seine Quelle im Text und nicht nur im
 * Kommentar.
 *
 * **Zur Form:** Die Inhalte sind in eigenen Worten zusammengefasst, nicht
 * übernommen. Wiedergegeben werden Sachverhalte und Zahlen — die sind frei —,
 * nicht die Darstellung der Vorlage.
 *
 * **Rechtlicher Vorbehalt zu den Marktzahlen:** Die E-Commerce-Studie
 * Österreich 2025 (KMU Forschung Austria im Auftrag des Handelsverbands) trägt
 * einen ausdrücklichen Vervielfältigungsvorbehalt. Übernommen sind deshalb nur
 * einzelne Kennzahlen als Faktenangabe mit voller Quellenangabe — keine
 * Tabellen, keine Grafiken, keine Textpassagen. **Vor dem Echtbetrieb ist die
 * Zustimmung des Handelsverbands einzuholen**; solange sie fehlt, lässt sich
 * `MARKT_AT` aus Kapitel 1 wieder herausnehmen, ohne dass der Rest leidet.
 *
 * Die Klassen (`case`, `case-figures`, `src` …) gehören zu `style.module.scss`
 * in diesem Ordner.
 */

/** Quellenzeile unter einem Baustein. */
const quelle = text => `<p class="src">Quelle: ${text}</p>`

const S4R_EC =
	'Skills4Retail, Modulheft „Introduction to E-commerce“, Abschnitt „Retail examples“ ' +
	'(Erasmus-Projekt 101111741, © 2023 Skills4Retail Consortium). Zusammenfassung in eigenen Worten.'

/**
 * Kapitel 1 — wie groß der Onlinehandel in Österreich wirklich ist.
 *
 * Steht bewusst am Ende von Kapitel 1, nach der eigenen Rechnung: Erst die
 * eigenen Zahlen, dann der Markt drumherum. Andersherum wirkt jede Junior
 * Company neben zwölf Milliarden Euro belanglos.
 */
export const MARKT_AT = `
	<h3>Wie groß ist der Markt in Österreich?</h3>
	<p>Eure 1.240 € aus dem Rechenbeispiel klingen klein. Sie sind es auch — und trotzdem
	ist der Markt, in dem ihr steht, kein anderer als der von Zalando oder Amazon. Ein paar
	Zahlen zur Einordnung:</p>

	<div class="case-figures">
		<div class="fig">
			<span class="n">12,5 Mrd €</span>
			<span class="k">geben Österreicher:innen ab 15 Jahren im Jahr im Distanzhandel aus —
			14 % mehr als im Vorjahr</span>
		</div>
		<div class="fig">
			<span class="n">2.080 €</span>
			<span class="k">davon im Schnitt pro Person und Jahr</span>
		</div>
		<div class="fig">
			<span class="n">69 %</span>
			<span class="k">der Bevölkerung ab 15 kaufen online ein; rund 6 Mio Menschen</span>
		</div>
		<div class="fig">
			<span class="n">33 %</span>
			<span class="k">der Ausgaben laufen über das Handy — 4,1 Mrd €</span>
		</div>
	</div>

	<p>Zwei Zahlen daraus sind für euch praktisch verwertbar. <b>Erstens:</b> Ein Drittel
	des Umsatzes entsteht am Smartphone. Wenn euer Shop am Handy schlecht aussieht, verliert
	ihr nicht ein paar Prozent, sondern ein Drittel. <b>Zweitens:</b> 51 % der Ausgaben gehen
	an ausländische Anbieter. Dass ihr aus der Region seid und die Ware selbst übergeben
	könnt, ist damit kein Nachteil gegenüber den Großen, sondern euer Unterschied.</p>

	<div class="note">
		<span class="ico i-merke">MERKT EUCH</span>
		<span>Der Anteil des Distanzhandels ist je Warengruppe völlig verschieden: Bei
		Lebensmitteln liegt er bei 2 %, bei Spielwaren, Sportartikeln, Elektrogeräten und
		Büchern über 30 %. Bevor ihr euch über eine niedrige Conversion Rate ärgert, schaut
		nach, ob eure Warengruppe online überhaupt gekauft wird.</span>
	</div>

	${quelle(
		'KMU Forschung Austria, E-Commerce-Studie Österreich 2025, im Auftrag des ' +
			'Handelsverbands Österreich (Erhebungszeitraum Mai 2024 bis April 2025). ' +
			'Einzelne Kennzahlen, wiedergegeben mit Quellenangabe.'
	)}
`

/**
 * Kapitel 7 — Zalando: Retouren senken, bevor sie entstehen.
 *
 * Passt hierher und nicht in Kapitel 10: Das Kapitel handelt vom Zurücknehmen,
 * und die Pointe der Fallstudie ist, dass die billigste Retoure die ist, die
 * gar nicht erst kommt.
 */
export const FALL_ZALANDO = `
	<aside class="case">
		<span class="case-tag">Fallstudie</span>
		<h4>Zalando: die Retoure verhindern, statt sie zu bearbeiten</h4>
		<p>Zalando ist Europas größte Online-Modeplattform — und hat dasselbe Problem wie ihr,
		nur tausendfach: Kleidung, die nicht passt, kommt zurück. Statt die Rücksendung
		billiger zu machen, arbeitet Zalando daran, dass sie gar nicht entsteht.</p>
		<ul class="bullets">
			<li><b>Größenberatung aus Erfahrungswerten.</b> Aus früheren Bestellungen und
			Rücksendungen einer Kundin errechnet das System, welche Größe bei <em>dieser</em>
			Marke passt — nicht welche Größe sie „hat“.</li>
			<li><b>Stilberatung.</b> Ein Dienst schlägt ganze Outfits vor, halb automatisch,
			halb von Menschen kuratiert.</li>
			<li><b>Bildersuche.</b> Wer ein Foto hochlädt, bekommt ähnliche Artikel gezeigt.</li>
		</ul>
		<p>Nach Angaben des Unternehmens sind die Rücksendungen wegen falscher Größe dadurch
		um über 10 % zurückgegangen, der durchschnittliche Bestellwert gestiegen.</p>
		<div class="case-transfer">
			<span class="lbl">Für eure Junior Company</span>
			<p>Ihr habt keine Algorithmen — aber dieselbe Stellschraube. Eine ehrliche
			Größentabelle mit <em>gemessenen</em> Werten („Brustumfang 96–100 cm“ statt „M“),
			ein Satz wie „fällt klein aus“ und ein Foto am Menschen statt auf dem Bügel kosten
			nichts und wirken in dieselbe Richtung. Jede Retoure, die ihr verhindert, spart
			euch Porto, Arbeit und ein Produkt, das ihr nicht mehr als neu verkaufen könnt.</p>
		</div>
		${quelle(S4R_EC)}
	</aside>
`

/**
 * Kapitel 9 — SHEIN und TikTok Shop: Social Commerce.
 *
 * Die einzige Fallstudie mit einem ausdrücklichen Gegenargument. Sie steht in
 * einem Kurs für 15- bis 19-Jährige, und beide Beispiele arbeiten mit
 * Mechanismen, die man kennen, aber nicht kopieren sollte.
 */
export const FALL_SHEIN_TIKTOK = `
	<aside class="case">
		<span class="case-tag">Fallstudie</span>
		<h4>SHEIN und TikTok Shop: wenn der Kanal der Laden ist</h4>
		<p>Bei den meisten Shops führt Social Media <em>zum</em> Verkauf hin. Bei diesen beiden
		findet er dort statt.</p>
		<div class="grid two">
			<div class="card">
				<span class="tag">SHEIN</span>
				<p>Die Modemarke misst laufend, was gerade im Netz gefragt ist, und lässt
				entsprechend produzieren. Beworben wird über tausende kleine Creator mit
				Affiliate-Links, dazu Live-Bereiche in der eigenen App und Punkte fürs Teilen
				und Bewerten.</p>
				<ul><li>Bis zu 60 % des Zugriffs kommen aus sozialen Medien</li><li>Befristete
				Aktionen erzeugen Dringlichkeit</li></ul>
			</div>
			<div class="card">
				<span class="tag">TikTok Shop</span>
				<p>Creator verkaufen direkt in der App — im Video oder im Livestream, mit
				Kaufknopf daneben. Marken richten eigene Schaufenster ein, Creator bekommen
				Provision.</p>
				<ul><li>Der Kauf passiert, ohne die App zu verlassen</li><li>Unterhaltung und
				Verkauf sind dasselbe Format</li></ul>
			</div>
		</div>
		<div class="note warnN">
			<span class="ico i-aber">ABER</span>
			<span>Beide Beispiele funktionieren auch mit Mitteln, die ihr <b>nicht</b> übernehmen
			solltet: künstlicher Zeitdruck, sehr kurze Produktzyklen, Belohnungen für positive
			Bewertungen. Bewertungen zu vergüten ist in Österreich als irreführende
			Geschäftspraktik unzulässig — siehe Kapitel 3. Und Ultra-Fast-Fashion ist genau das
			Gegenteil dessen, was die meisten Junior Companies als Nachhaltigkeitsziel angeben.</span>
		</div>
		<div class="case-transfer">
			<span class="lbl">Für eure Junior Company</span>
			<p>Übernehmbar ist der <em>Mechanismus</em>, nicht das Tempo: Zeigt das Produkt in
			Benutzung statt im Katalogfoto, lasst Mitschüler:innen davon erzählen statt euch
			selbst, und macht den Weg vom Beitrag zur Bestellung so kurz wie möglich — ein
			Link, der direkt auf die Produktseite führt, nicht auf die Startseite.</p>
		</div>
		${quelle(S4R_EC)}
	</aside>
`

/**
 * Kapitel 10 — Decathlon: online und offline als ein Kanal.
 *
 * Steht in Kapitel 10, weil dort der Abschnitt „Omnichannel: Schulmesse trifft
 * Onlineshop“ liegt — die Fallstudie ist dessen großes Vorbild.
 */
export const FALL_DECATHLON = `
	<aside class="case">
		<span class="case-tag">Fallstudie</span>
		<h4>Decathlon: ein Lager, zwei Wege zur Kundin</h4>
		<p>Der französische Sportartikelhändler betreibt über 1.700 Filialen und einen
		Onlineshop — aber nicht als zwei Geschäfte nebeneinander. Beide greifen auf denselben
		Bestand zu, und das ist der ganze Trick.</p>
		<ul class="bullets">
			<li><b>Online bestellen, im Laden abholen</b> — oft am selben Tag.</li>
			<li><b>Der Shop zeigt, was die Filiale nebenan vorrätig hat.</b> Wer es eilig hat,
			fährt hin, statt zu warten.</li>
			<li><b>Ein Warenkorb über alle Geräte</b> — am Handy stöbern, am Rechner bestellen.</li>
			<li><b>Ein Kundenkonto für beides</b>, kein getrenntes Treueprogramm.</li>
			<li><b>Rücknahme zum Wiederverkauf</b> statt zur Entsorgung.</li>
		</ul>
		<p>Der Effekt ist nicht, dass online den Laden ersetzt: In vielen Regionen kommen durch
		die Abholung <em>mehr</em> Menschen in die Filiale — und kaufen dort etwas dazu.</p>
		<div class="case-transfer">
			<span class="lbl">Für eure Junior Company</span>
			<p>Ihr habt beides längst: euren Shop und den Stand bei der Schulmesse. Behandelt sie
			wie Decathlon als <em>einen</em> Kanal. Nehmt am Stand Bestellungen für Artikel auf,
			die gerade aus sind. Legt eine Karte mit Shop-Adresse und QR-Code in jede Tüte.
			Führt <b>eine</b> Bestandsliste, nicht zwei — sonst verkauft ihr am Stand, was online
			gerade weggegangen ist. Und zählt am Ende zusammen, welcher Weg was gebracht hat:
			Das ist genau die Auswertung, die in Feld 13 gehört.</p>
		</div>
		${quelle(S4R_EC)}
	</aside>
`
